import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { X, Rocket, Mail, Check, Loader2, ChevronRight } from "lucide-react";

const DISMISS_KEY = "rivones_coming_soon_dismissed_v1";
const SUBSCRIBED_KEY = "rivones_waitlist_subscribed_v1";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComingSoonBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      let dismissed = false;
      if (raw) {
        const ts = Number.parseInt(raw, 10);
        if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS) {
          dismissed = true;
        } else {
          // expired or legacy "1" value — clear so banner reappears
          localStorage.removeItem(DISMISS_KEY);
        }
      }
      const subscribed = localStorage.getItem(SUBSCRIBED_KEY) === "1";
      setVisible(!dismissed);
      if (subscribed) setStatus("success");
    } catch {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (expanded && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [expanded]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const { data } = await axios.post(`${API}/waitlist/signup`, {
        email: email.trim(),
        source: typeof window !== "undefined" ? window.location.pathname : "banner",
      });
      try {
        localStorage.setItem(SUBSCRIBED_KEY, "1");
      } catch {
        /* ignore */
      }
      setStatus("success");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === "string" ? detail : t("comingSoon.error"));
      setStatus("error");
    }
  };

  if (!visible) return null;

  return (
    <div
      className="sticky top-0 z-[60] text-white shadow-md border-b border-white/10 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-amber-400"
      data-testid="coming-soon-banner"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-1 sm:py-2 flex items-center gap-2 sm:gap-3 min-h-[28px] sm:min-h-[36px]">
        <Rocket className="w-3.5 h-3.5 sm:w-5 sm:h-5 flex-shrink-0 hidden xs:inline-block text-white drop-shadow" />

        {/* Collapsed message + CTA */}
        {!expanded && status !== "success" && (
          <>
            <p className="flex-1 text-[11px] sm:text-sm leading-tight min-w-0 text-white">
              <span className="font-bold text-white">{t("comingSoon.soon")}</span>
              <span className="hidden sm:inline text-white/95">{t("comingSoon.detail")}</span>
              <span className="sm:hidden text-white/95"> · </span>
              <span className="font-bold text-white">{t("comingSoon.launchDate")}</span>
            </p>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-violet-700 hover:bg-amber-100 text-xs font-bold transition-colors whitespace-nowrap shadow-md"
              data-testid="coming-soon-banner-cta"
            >
              {t("comingSoon.joinWaitlist")}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="sm:hidden inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white text-violet-700 hover:bg-amber-100 text-[10px] font-bold transition-colors whitespace-nowrap shadow-md"
              data-testid="coming-soon-banner-cta-mobile"
              aria-label={t("comingSoon.joinWaitlist")}
            >
              {t("comingSoon.join")}
            </button>
          </>
        )}

        {/* Expanded inline email form */}
        {expanded && status !== "success" && (
          <form
            onSubmit={handleSubmit}
            className="flex-1 flex items-center gap-2 min-w-0"
            data-testid="coming-soon-banner-form"
          >
            <Mail className="w-4 h-4 flex-shrink-0 hidden sm:inline-block text-terracotta" />
            <input
              ref={inputRef}
              type="email"
              required
              autoComplete="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              disabled={status === "loading"}
              className="flex-1 min-w-0 bg-white text-neutral-900 placeholder-neutral-400 px-3 py-1.5 rounded-full text-xs sm:text-sm outline-none focus:ring-2 focus:ring-terracotta disabled:opacity-60"
              data-testid="coming-soon-banner-email"
            />
            <button
              type="submit"
              disabled={status === "loading" || !email.trim()}
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full bg-terracotta text-white text-xs sm:text-sm font-semibold hover:bg-terracotta/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              data-testid="coming-soon-banner-submit"
            >
              {status === "loading" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <>
                  <span className="hidden sm:inline">{t("comingSoon.notifyMe")}</span>
                  <span className="sm:hidden">{t("comingSoon.go")}</span>
                </>
              )}
            </button>
            {errorMsg && (
              <span
                className="hidden md:inline text-[11px] text-red-300 font-medium max-w-[200px] truncate"
                data-testid="coming-soon-banner-error"
              >
                {errorMsg}
              </span>
            )}
          </form>
        )}

        {/* Success state */}
        {status === "success" && (
          <p
            className="flex-1 text-xs sm:text-sm leading-snug font-semibold flex items-center gap-2 text-white"
            data-testid="coming-soon-banner-success"
          >
            <span className="inline-flex w-5 h-5 rounded-full bg-emerald-500 items-center justify-center">
              <Check className="w-3.5 h-3.5 text-white" />
            </span>
            <span>{t("comingSoon.success")}</span>
          </p>
        )}

        <button
          type="button"
          aria-label={t("comingSoon.close")}
          onClick={handleDismiss}
          className="p-0.5 sm:p-1 rounded-full hover:bg-white/15 transition-colors flex-shrink-0 text-white/80 hover:text-white"
          data-testid="coming-soon-banner-dismiss"
        >
          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </button>
      </div>

      {/* Mobile error line (banner stays single-row, error appears below) */}
      {errorMsg && expanded && status !== "success" && (
        <div className="md:hidden max-w-7xl mx-auto px-3 sm:px-4 pb-1.5">
          <p className="text-[11px] text-red-300" data-testid="coming-soon-banner-error-mobile">
            {errorMsg}
          </p>
        </div>
      )}
    </div>
  );
}
